import { Injectable, signal, computed, effect } from '@angular/core';
import { Observable, from } from 'rxjs';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { connect, getAccount, reconnect, switchChain, watchAccount } from 'wagmi/actions';
import { environment } from '../../environments/environment';
import { WalletState } from '../interface/wallet.state';
import { LocalStorage } from './local-storage';
import { polygonAmoy } from '@reown/appkit/networks'; // required default network
import { Config } from 'wagmi';
import { ToastrService } from 'ngx-toastr';
import { injected } from 'wagmi/connectors';

@Injectable({
  providedIn: 'root'
})
export class AppKitService {
  walletState = signal<WalletState>({
    isConnected: false,
    address: null,
    network: null,
    isSupportedNetwork: false,
    connectionType: null
  });

  walletAddress = computed(() => this.walletState().address);
  connected = computed(() => this.walletState().isConnected);
  private _walletAddress = signal<string>('');
  private isDisconnecting = false;
  private isConnecting = false; // Guards watchAccount from firing toast during connectWallet()
  appKit: any;
  wagmiAdapter!: WagmiAdapter;
  wagmiConfig!: Config;
  private supportedNetworks: any = [polygonAmoy];
  private defaultNetworks: any = [polygonAmoy];
  private projectId = environment.PROJECT_ID;
  private metadata = {
    name: 'Polymarket',
    description: 'Polymarket',
    url: environment.PROJECT_URL,
    icons: ['https://polymarket.blockchainx.tech/assets/images/Blockchain logo.svg']
  };
  private initializationPromise: Promise<void>;
  private isInitialized = false;
  private isAuthorizedUser = signal(false);

  isAuthorized = computed(() => this.isAuthorizedUser());

  constructor(private localstorage: LocalStorage, private toastr: ToastrService) {
    this.initializationPromise = this.initializeAppKit();

    // Sync wallet address to local storage
    effect(() => {
      const address = this.walletAddress();
      if (address) {
        this.localstorage.storeData('walletAddress', address);
      } else {
        this.localstorage.removeItem('walletAddress');
      }
    });
  }

  private async initializeAppKit() {
    this.wagmiAdapter = new WagmiAdapter({
      projectId: this.projectId,
      networks: this.defaultNetworks
    });

    this.appKit = createAppKit({
      adapters: [this.wagmiAdapter],
      networks: this.defaultNetworks,
      metadata: this.metadata,
      projectId: this.projectId,
      features: { analytics: true },
      allWallets: 'HIDE'
    });

    this.wagmiConfig = this.wagmiAdapter.wagmiConfig as any;

    // Explicitly attempt to reconnect the last session
    try {
      await reconnect(this.wagmiConfig);
    } catch (e) {
      console.warn('Initial reconnection failed:', e);
    }

    const account = getAccount(this.wagmiConfig);
    if (account?.address && !this.isAdmin(account.address)) {
      await this.disconnect();
    } else {
      this.walletState.set({
        isConnected: !!account?.address,
        address: account?.address || null,
        network: account?.chainId || null,
        isSupportedNetwork: true,
        connectionType: account?.connector?.name || null
      });
    }

    this.isInitialized = true;

    // Watch for account changes (e.g. user switches wallet in MetaMask)
    watchAccount(this.wagmiConfig, {
      onChange: async (account) => {
        if (!account?.address) return;

        // Skip during an active connectWallet() — it handles validation and toast itself
        if (this.isConnecting) return;

        await this.updateWalletState(false);
      }
    });
  }

  async connectWallet(): Promise<void> {
    await this.waitForInitialization();
    const hasMetaMask =
      typeof window !== 'undefined' &&
      typeof (window as any).ethereum !== 'undefined' &&
      !!(window as any).ethereum.isMetaMask;

    if (!hasMetaMask) {
      this.toastr.error('MetaMask is not installed. Please install MetaMask.');
      return;
    }

    try {
      this.isConnecting = true;

      await connect(this.wagmiConfig, {
        connector: injected({ target: 'metaMask' })
      });

      // Validate admin status and update state.
      // Pass true so a success toast is shown only after confirmed authorization.
      await this.updateWalletState(true);
    } catch (error: any) {
      const userRejected =
        error?.code === 4001 ||
        error?.message?.toLowerCase().includes('user rejected');

      if (!userRejected) {
        this.toastr.error('Failed to connect MetaMask');
      }
    } finally {
      this.isConnecting = false;
    }
  }

  private isAdmin(address: string | undefined | null): boolean {
    if (!address) return false;

    return environment.ADMIN_WALLET_ADDRESSES
      .map(a => a.toLowerCase())
      .includes(address.toLowerCase());
  }

  /**
   * updateWalletState
   * @param showSuccessToast - only true when called from connectWallet(), ensuring
   *   the "Wallet connected" toast fires exactly once, after full validation.
   */
  private async updateWalletState(showSuccessToast: boolean = false) {
    if (this.isDisconnecting) return;

    const account = getAccount(this.wagmiConfig);

    // Non-admin wallet → block and notify
    if (account?.address && !this.isAdmin(account.address)) {
      this.isAuthorizedUser.set(false);
      this.walletState.set({
        isConnected: false,
        address: null,
        network: null,
        isSupportedNetwork: false,
        connectionType: null
      });

      this.toastr.error('Unauthorized wallet address');

      await this.disconnect();
      return;
    }

    // Admin wallet → allow and optionally show success toast
    this.isAuthorizedUser.set(true);

    this.walletState.set({
      isConnected: !!account?.address,
      address: account?.address || null,
      network: account?.chainId || null,
      isSupportedNetwork: account?.chainId === polygonAmoy.id,
      connectionType: account?.connector?.name || null
    });

    if (showSuccessToast && account?.address) {
      this.toastr.success('Wallet connected successfully');
    }
  }

  async disconnect(): Promise<void> {
    this.isDisconnecting = true;

    await this.appKit.disconnect();

    this._walletAddress.set('');
    this.walletState.set({
      isConnected: false,
      address: null,
      network: null,
      isSupportedNetwork: false,
      connectionType: null
    });

    this.localstorage.removeItem('walletAddress');

    // small delay to avoid watcher race condition
    setTimeout(() => {
      this.isDisconnecting = false;
    }, 500);
  }

  async switchChain(requiredChainId: number): Promise<void> {
    await this.waitForInitialization();
    const currentAccount = getAccount(this.wagmiConfig);
    
    console.log('switchChain - Current state:', {
      chainId: currentAccount.chainId,
      connector: currentAccount.connector?.name,
      status: currentAccount.status
    });

    if (!currentAccount.connector) {
      console.warn('switchChain: No active connector found. Attempting to reconnect...');
      await reconnect(this.wagmiConfig);
    }
    
    const updatedAccount = getAccount(this.wagmiConfig);
    if (updatedAccount.connector && updatedAccount.chainId !== requiredChainId) {
      console.log(`switchChain: Requesting switch to ${requiredChainId}`);
      await switchChain(this.wagmiConfig, { chainId: requiredChainId });
    } else if (!updatedAccount.connector) {
      throw new Error('Connector not connected. Please reconnect your wallet.');
    }
  }

  /** Reconnect wallet manually */
  async reconnectWallet(): Promise<void> {
    await this.waitForInitialization();
    await reconnect(this.wagmiConfig);
  }

  getAddress(): string | null {
    return this.walletAddress() || null;
  }

  fetchConfig() {
    return this.wagmiConfig;
  }

  setWalletAddress(address: string) {
    this._walletAddress.set(address);
  }

  getWalletAddress(): string {
    return this._walletAddress();
  }

  getCurrentWalletState(): WalletState {
    return this.walletState();
  }

  getWalletState(): Observable<WalletState> {
    return from(Promise.resolve(this.walletState()));
  }

  async waitForInitialization(): Promise<void> {
    await this.initializationPromise;
  }

  getSafeWalletAddress(): string | null {
    const address = this.walletState().address;

    if (!address) return null;

    const isAdmin = environment.ADMIN_WALLET_ADDRESSES
      .map(a => a.toLowerCase())
      .includes(address.toLowerCase());

    return isAdmin ? address : null;
  }
}
